import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, FileText, Paperclip, Plus, Upload } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { displayUnit } from "@/utils/displayUnit";
import { acceptedQtyFromPct, completionPctFromQty } from "@/features/deliveries/utils/pockMath";
import { useCloseOrder } from "../hooks/useOrders";
import type { Order } from "../types";

/**
 * „POCK bez kwot” — przekazanie wykonanych prac przez pracownika GK z nadanym dostępem
 * „Realizacja” (widok jak u podwykonawcy). Pracownik wystawia protokół z pozycjami
 * i ilością wykonaną, a potem dokłada załączniki do pozycji. Kwot nie ma nigdzie
 * (backend zeruje i maskuje), a z takiego POCK nie da się wystawić faktury ani UZ/UoD —
 * rozliczenie robi GK własnym protokołem. Dostępne dla każdego typu zamówienia.
 */

interface AttachmentVersion {
  id: string;
  original_name: string;
  is_superseded: boolean;
  created_at: string;
  managed_file_detail: { url: string | null; original_filename: string } | null;
}

interface ExecutorPockItem {
  id: string;
  order_line_item: string;
  order_line_item_name: string;
  order_line_item_unit: string;
  order_line_item_quantity: string;
  completion_percentage: string;
  attachment_versions: AttachmentVersion[];
}

export interface ExecutorPock {
  id: string;
  protocol_number: string;
  is_final: boolean;
  is_withdrawn: boolean;
  without_amounts?: boolean;
  accepted_by_contractor: boolean | null;
  created_by: string;
  created_by_name: string;
  created_at: string;
  notes: string;
  items: ExecutorPockItem[];
}

const CREATE_BLOCKED_STATUSES = new Set(["sent", "cancelled", "closed"]);

const fmtQty = (v: string | number) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (isNaN(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
};

function flattenErrors(errors: unknown): string {
  if (!errors) return "";
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.map(flattenErrors).filter(Boolean).join(" ");
  if (typeof errors === "object") return Object.values(errors as Record<string, unknown>).map(flattenErrors).filter(Boolean).join(" ");
  return String(errors);
}

interface Props {
  order: Order;
}

export function ExecutorPockSection({ order }: Props) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [showForm, setShowForm] = useState(false);
  const [isFinal, setIsFinal] = useState(!order.allow_partial_pock);
  const [notes, setNotes] = useState("");
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [created, setCreated] = useState<ExecutorPock | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Zamknięcie zamówienia przejętego przez pracownika: po końcowym POCK bez kwot, gdy
  // nie ma nierozliczonych protokołów z kwotami (backend: OrderViewSet.close).
  const [closeNote, setCloseNote] = useState("Zrealizowane przez pracownika GK ELEKTIM — bez rozliczenia z podwykonawcą");
  const closeOrder = useCloseOrder();

  const { data: protocols = [] } = useQuery<ExecutorPock[]>({
    queryKey: ["protocols", { order: order.id }],
    queryFn: () =>
      apiClient.get<{ data: ExecutorPock[] }>("/protocols/", { params: { order: order.id } }).then((r) => r.data.data),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["protocols", { order: order.id }] });
    qc.invalidateQueries({ queryKey: ["orders", order.id] });
  };

  const create = useMutation({
    mutationFn: (payload: unknown) =>
      apiClient.post<{ data: ExecutorPock }>("/protocols/", payload).then((r) => r.data.data),
    onSuccess: (data) => {
      invalidate();
      setCreated(data);
      setShowForm(false);
    },
    onError: (err: { response?: { data?: { errors?: unknown } } }) => {
      setError(flattenErrors(err?.response?.data?.errors) || "Nie udało się wystawić POCK.");
    },
  });

  const withdraw = useMutation({
    mutationFn: (protocolId: string) => apiClient.post(`/protocols/${protocolId}/withdraw/`),
    onSuccess: invalidate,
  });

  const openForm = () => {
    const initial: Record<string, string> = {};
    for (const li of order.line_items) initial[li.id] = li.quantity;
    setQtys(initial);
    setIsFinal(!order.allow_partial_pock);
    setNotes("");
    setError("");
    setCreated(null);
    setShowForm(true);
  };

  const submit = () => {
    setError("");
    const items = order.line_items
      .filter((li) => (parseFloat(qtys[li.id] ?? "0") || 0) > 0)
      .map((li) => ({
        order_line_item: li.id,
        // completion_percentage = wykonano / zamówiono × 100 (pełna precyzja kolumny — patrz pockMath).
        completion_percentage: completionPctFromQty(qtys[li.id] ?? "0", li.quantity, "100"),
      }));
    if (items.length === 0) {
      setError("Podaj ilość wykonaną co najmniej dla jednej pozycji.");
      return;
    }
    create.mutate({ order: order.id, is_final: isFinal, notes, items });
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const createBlocked = CREATE_BLOCKED_STATUSES.has(order.status);
  const isOwn = (p: ExecutorPock) => !!p.without_amounts && p.created_by === user?.id;
  const canCloseOrder = order.status === "accepted_final" && !!order.finished_without_settlement;

  return (
    <div className="mt-8" data-testid="executor-pock-section">
      {canCloseOrder && (
        <div className="bg-white dark:bg-gray-800 p-4 mb-4 border-l-4 border-green-500" data-testid="executor-close-order">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Zamówienie zakończone POCK-iem bez kwot</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Wszystkie pozycje zostały przekazane, a nie ma nierozliczonych protokołów z kwotami — nie będzie faktury.
            Zamknij zamówienie (albo zostaw: automat zamknie je po 30 dniach bez zmian).
          </p>
          <textarea
            value={closeNote}
            onChange={(e) => setCloseNote(e.target.value)}
            rows={2}
            aria-label="Uzasadnienie zamknięcia"
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm mb-3"
          />
          {closeOrder.isError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-2">Nie udało się zamknąć zamówienia.</p>
          )}
          <Button
            size="sm"
            loading={closeOrder.isPending}
            disabled={!closeNote.trim()}
            onClick={() => closeOrder.mutate({ id: order.id, settlement_note: closeNote.trim() })}
          >
            Zamknij zamówienie
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-l-4 border-[var(--color-primary)] pl-3">
          POCK bez kwot — przekazanie wykonanych prac
        </h2>
        {!showForm && !created && (
          <Button
            size="sm"
            onClick={openForm}
            disabled={createBlocked}
            title={createBlocked ? "POCK zakłada się tylko do zamówienia przyjętego lub w realizacji" : undefined}
          >
            <Plus size={14} /> Nowy POCK bez kwot
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Wybierz pozycje i ilość wykonaną, wystaw protokół, a potem dołącz pliki do pozycji. Taki POCK nie ma kwot —
        tylko pozycje i procent wykonania — i nie da się do niego wystawić faktury ani UZ/UoD.
      </p>

      {showForm && (
        <div className="bg-white dark:bg-gray-800 p-4 mb-4 border-l-4 border-[var(--color-primary)]">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Nowy POCK bez kwot</h3>
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-xs min-w-[420px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="text-left px-3 py-2 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                  <th className="text-right px-3 py-2 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono</th>
                  <th className="text-right px-3 py-2 font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Wykonano</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {order.line_items.map((li) => (
                  <tr key={li.id}>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{li.line_item_type_name}</td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {fmtQty(li.quantity)} {displayUnit(li.line_item_type_unit)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        max={parseFloat(li.quantity)}
                        step="0.01"
                        aria-label={`Wykonano: ${li.line_item_type_name}`}
                        value={qtys[li.id] ?? ""}
                        readOnly={!order.allow_partial_pock}
                        onChange={(e) => {
                          if (order.allow_partial_pock) setQtys((prev) => ({ ...prev, [li.id]: e.target.value }));
                        }}
                        className={`w-24 text-right rounded border px-2 py-1 text-sm ${
                          !order.allow_partial_pock
                            ? "bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        }`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!order.allow_partial_pock && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
              Umowa ramowa nie zezwala na częściowy POCK — protokół obejmuje wszystkie pozycje w całości (końcowy).
            </p>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-3">
            <input
              type="checkbox"
              checked={isFinal}
              disabled={!order.allow_partial_pock}
              onChange={(e) => setIsFinal(e.target.checked)}
              className="rounded"
            />
            POCK końcowy (kończy zamówienie)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Uwagi (opcjonalnie)…"
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm mb-3"
          />
          {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={submit} loading={create.isPending}>
              Wystaw POCK bez kwot
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} disabled={create.isPending}>
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {created && (
        <div className="bg-white dark:bg-gray-800 p-4 mb-4 border-l-4 border-green-500">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            POCK <span className="font-medium">{created.protocol_number}</span> został wystawiony. Dołącz teraz pliki
            do pozycji — albo wróć do tego później z listy poniżej.
          </p>
          <div className="flex flex-col gap-2">
            {created.items.map((item) => (
              <ItemAttachmentRow key={item.id} protocolId={created.id} item={item} onUploaded={invalidate} />
            ))}
          </div>
          <div className="mt-3">
            <Button size="sm" onClick={() => setCreated(null)}>Zakończ</Button>
          </div>
        </div>
      )}

      {protocols.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak protokołów dla tego zamówienia.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {protocols.map((p) => {
            const open = expanded.has(p.id);
            const own = isOwn(p);
            return (
              <div key={p.id} className={`bg-white dark:bg-gray-800 ${p.is_withdrawn ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between pr-3">
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    className="flex-1 flex items-center gap-3 flex-wrap px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/70"
                  >
                    <FileText size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                    <span className={`font-medium ${p.is_withdrawn ? "line-through text-gray-400" : "text-gray-900 dark:text-gray-100"}`}>
                      {p.protocol_number}
                    </span>
                    {p.is_withdrawn ? (
                      <Badge color="gray">Wycofany</Badge>
                    ) : (
                      <Badge color={p.is_final ? "purple" : "blue"}>{p.is_final ? "Końcowy" : "Częściowy"}</Badge>
                    )}
                    {p.without_amounts && <Badge color="orange">Bez kwot</Badge>}
                    {/* POCK bez kwot nie czeka na odpowiedź podwykonawcy — bez badge'a odpowiedzi. */}
                    {!p.is_withdrawn && !p.without_amounts && (
                      <Badge color={p.accepted_by_contractor === null ? "yellow" : p.accepted_by_contractor ? "green" : "red"}>
                        {p.accepted_by_contractor === null ? "Oczekuje" : p.accepted_by_contractor ? "Zaakceptowany" : "Odrzucony"}
                      </Badge>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {p.created_by_name} · {new Date(p.created_at).toLocaleDateString("pl")}
                    </span>
                    {open ? <ChevronUp size={16} className="text-gray-400 ml-auto" /> : <ChevronDown size={16} className="text-gray-400 ml-auto" />}
                  </button>
                  {own && !p.is_withdrawn && p.accepted_by_contractor === null && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Wycofać POCK ${p.protocol_number}?`)) withdraw.mutate(p.id);
                      }}
                      disabled={withdraw.isPending}
                      className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-2 py-1 whitespace-nowrap disabled:opacity-50"
                    >
                      Wycofaj
                    </button>
                  )}
                </div>
                {open && (
                  <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 flex flex-col gap-2">
                    {p.notes && <p className="text-xs text-gray-500 dark:text-gray-400">{p.notes}</p>}
                    {p.items.map((item) =>
                      own ? (
                        <ItemAttachmentRow key={item.id} protocolId={p.id} item={item} onUploaded={invalidate} />
                      ) : (
                        <ItemSummary key={item.id} item={item} />
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function currentFiles(item: ExecutorPockItem): AttachmentVersion[] {
  return (item.attachment_versions ?? []).filter((v) => !v.is_superseded);
}

function ItemSummary({ item }: { item: ExecutorPockItem }) {
  const done = acceptedQtyFromPct(item.completion_percentage, item.order_line_item_quantity);
  const files = currentFiles(item);
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-gray-800 dark:text-gray-200">{item.order_line_item_name}</span>
        <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">
          wykonano {fmtQty(done)} / {fmtQty(item.order_line_item_quantity)} {displayUnit(item.order_line_item_unit)}
        </span>
      </div>
      <FileLinks files={files} />
    </div>
  );
}

function FileLinks({ files }: { files: AttachmentVersion[] }) {
  if (files.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {files.map((f) => (
        <a
          key={f.id}
          href={f.managed_file_detail?.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          download={f.original_name}
          className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
        >
          <Paperclip size={12} /> {f.original_name || f.managed_file_detail?.original_filename}
        </a>
      ))}
    </div>
  );
}

/** Pozycja własnego POCK: ilość wykonana + wgrywanie plików (backend: PATCH …/items/{id}/attachment/). */
function ItemAttachmentRow({
  protocolId,
  item,
  onUploaded,
}: {
  protocolId: string;
  item: ExecutorPockItem;
  onUploaded: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const done = acceptedQtyFromPct(item.completion_percentage, item.order_line_item_quantity);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    const fd = new FormData();
    for (const f of files) fd.append("attachment", f);
    try {
      await apiClient.patch(`/protocols/${protocolId}/items/${item.id}/attachment/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded();
    } catch {
      setError("Błąd wgrywania");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-gray-800 dark:text-gray-200">
          {item.order_line_item_name}
          <span className="text-gray-500 dark:text-gray-400 ml-2 text-xs whitespace-nowrap">
            wykonano {fmtQty(done)} / {fmtQty(item.order_line_item_quantity)} {displayUnit(item.order_line_item_unit)}
          </span>
        </span>
        <div className="flex items-center gap-2">
          {error && <span className="text-xs text-red-500">{error}</span>}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] border border-[var(--color-primary)] rounded px-2 py-1 hover:bg-[var(--color-primary-dim)] disabled:opacity-50"
          >
            <Upload size={12} />
            {uploading ? "Wgrywanie…" : "Dodaj załącznik"}
          </button>
          <input ref={fileRef} type="file" multiple className="hidden" aria-label={`Załącznik: ${item.order_line_item_name}`} onChange={handleFiles} />
        </div>
      </div>
      <FileLinks files={currentFiles(item)} />
    </div>
  );
}
